// Live updates for one galaxy (04 §7): ticket → socket, RESUME after reconnects, full refetch on
// FORCE_RESYNC_REQUIRED, PING every 25 s, reconnect with backoff. Events only invalidate queries,
// coalesced per burst (api/invalidate.ts); REST stays the source of truth.
import type { ServerFrame } from '@stars-and-ships/api-types';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { endpoints } from '../api/endpoints';
import { invalidateSoon } from '../api/invalidate';
import { queryKeys } from '../api/queries';
import { useChannels } from '../state/channels';
import { useSession } from '../state/session';

const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

export function applyEvent(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  galaxyId: string,
  frame: ServerFrame
): void {
  switch (frame.event) {
    case 'FORCE_RESYNC_REQUIRED':
      void queryClient.invalidateQueries();
      return;
    case 'MAP_CHANGED':
    case 'SYSTEM_OWNER_CHANGED':
    case 'PLAYER_SHIELD_CHANGED':
      invalidateSoon(queryClient, [queryKeys.map(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      return;
    case 'SYSTEM_STATE_UPDATED':
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      return;
    case 'BUILDING_COMPLETED':
    case 'PORT_TRADE_COMPLETED':
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      return;
    case 'NOTIFICATION_CREATED':
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.notifications(galaxyId)]);
      return;
    case 'SHIELD_WARNING':
    case 'SHIELD_EXPIRED':
    case 'DARK_MATTER_CHANGED':
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      return;
    case 'SHIP_PRODUCED':
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
      return;
    case 'FLEET_DEPARTED':
    case 'FLEET_ARRIVED':
    case 'FLEET_RETURNED':
    case 'FLEET_EJECTED':
    case 'FLEET_INCOMING':
    case 'FLEET_INCOMING_CANCELLED':
      invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      return;
    case 'FLEET_ORDERS_CHANGED':
      invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      return;
    case 'ALLIANCE_ATTACKS_CHANGED':
      invalidateSoon(queryClient, [queryKeys.alliances(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
      return;
    case 'SCOUT_RETURNED':
    case 'SCOUT_FAILED_AGAINST_YOU':
    case 'INTEL_REPORT_SHARED':
      invalidateSoon(queryClient, [queryKeys.intel(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      return;
    case 'CLAIM_PLACED':
    case 'CLAIM_ACTIVATED':
    case 'CLAIM_RESOLVED':
      invalidateSoon(queryClient, [queryKeys.claims(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      return;
    case 'BATTLE_STARTED':
    case 'BATTLE_ROUND_RESOLVED':
    case 'BATTLE_FLEET_JOINED':
    case 'BATTLE_RETREAT_ORDERED':
    case 'BATTLE_ENDED':
    case 'BATTLE_WAITING_CHANGED':
      invalidateSoon(queryClient, [queryKeys.battles(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      return;
    case 'ALLIANCE_MEMBERSHIP_CHANGED':
    case 'ALLIANCE_UPDATED':
    case 'ALLIANCE_DISBANDED':
      // Membership changes what the player sees everywhere (systems, fleets, intel, battles).
      invalidateSoon(queryClient, [queryKeys.alliances(galaxyId)]);
      invalidateSoon(queryClient, [queryKeys.galaxyMe(galaxyId)]);
      if (frame.event !== 'ALLIANCE_UPDATED') {
        invalidateSoon(queryClient, [queryKeys.fleets(galaxyId)]);
        invalidateSoon(queryClient, [queryKeys.intel(galaxyId)]);
        invalidateSoon(queryClient, [queryKeys.battles(galaxyId)]);
        invalidateSoon(queryClient, [queryKeys.systems(galaxyId)]);
      }
      return;
    case 'ALLIANCE_INVITE_RECEIVED':
    case 'ALLIANCE_MOTD_CHANGED':
      invalidateSoon(queryClient, [queryKeys.alliances(galaxyId)]);
      return;
    default:
      return;
  }
}

export function useGalaxySocket(galaxyId: string | undefined): void {
  const queryClient = useQueryClient();
  const status = useSession((s) => s.status);

  useEffect(() => {
    if (!galaxyId || status !== 'authenticated') return;
    let socket: WebSocket | null = null;
    let stopped = false;
    let attempt = 0;
    let lastSeq = 0;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    let reconnect: ReturnType<typeof setTimeout> | undefined;
    // Explicit channels (system:<id>) this socket has asked for; reset by every new connection.
    let ready = false;
    let subscribed = new Set<string>();
    const syncChannels = () => {
      if (!ready || socket?.readyState !== WebSocket.OPEN) return;
      const wanted = new Set(Object.keys(useChannels.getState().wanted));
      for (const channel of wanted) {
        if (subscribed.has(channel)) continue;
        socket.send(JSON.stringify({ type: 'SUBSCRIBE', channel, correlationId: channel }));
        subscribed.add(channel);
      }
      for (const channel of subscribed) {
        if (wanted.has(channel)) continue;
        socket.send(JSON.stringify({ type: 'UNSUBSCRIBE', channel }));
        subscribed.delete(channel);
      }
    };
    const stopChannelSync = useChannels.subscribe(syncChannels);

    const scheduleReconnect = () => {
      if (stopped) return;
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt++);
      reconnect = setTimeout(() => void connect(), delay);
    };

    const onFrame = (frame: ServerFrame) => {
      if (frame.event === 'CONNECTION_READY') {
        attempt = 0;
        const serverSeq = (frame.payload as { lastSequenceId: number }).lastSequenceId;
        if (lastSeq > 0) socket?.send(JSON.stringify({ type: 'RESUME', lastSequenceId: lastSeq }));
        else lastSeq = serverSeq;
        heartbeat = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'PING', clientTime: Date.now() }));
          }
        }, HEARTBEAT_MS);
        ready = true;
        subscribed = new Set();
        syncChannels();
        return;
      }
      if (frame.channel !== 'connection' && frame.seq > lastSeq) lastSeq = frame.seq;
      applyEvent(queryClient, galaxyId, frame);
    };

    const connect = async () => {
      try {
        const { ticket } = await endpoints.wsTicket(galaxyId);
        if (stopped) return;
        const url = new URL('/ws', window.location.href);
        url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
        url.searchParams.set('ticket', ticket);
        socket = new WebSocket(url);
        socket.onmessage = (message) => {
          try {
            onFrame(JSON.parse(String(message.data)) as ServerFrame);
          } catch {
            // ignore malformed frames
          }
        };
        socket.onclose = () => {
          ready = false;
          clearInterval(heartbeat);
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    void connect();
    return () => {
      stopped = true;
      stopChannelSync();
      clearTimeout(reconnect);
      clearInterval(heartbeat);
      socket?.close(1000, 'client closed');
    };
  }, [galaxyId, status, queryClient]);
}
